import { useEffect, useRef, useState } from "react";
import { useFrame } from "@react-three/fiber";
import { assertNever } from "@reagent/sim";
import type { FxInstance } from "./types";
import {
  BurstSpill,
  CardSlam,
  CorrosionMark,
  Explosion,
  HeatShimmer,
  LeakDribble,
  MergeFlash,
  ObjectiveTick,
  PoisonPuff,
  PressureJudder,
  ReactionFlash,
  SludgeBubble,
  WarningPulse,
} from "./effects";

/**
 * Owns the pool of live effects. Each instance gets a progress ref that this
 * layer advances every frame; effects read it rather than tracking their own
 * clocks, so they all share one timebase and expire predictably.
 */
export function FxLayer({ instances, onExpire }: { instances: FxInstance[]; onExpire: (id: string) => void }) {
  return (
    <group>
      {instances.map((fx) => (
        <FxInstanceView key={fx.id} fx={fx} onExpire={onExpire} />
      ))}
    </group>
  );
}

function FxInstanceView({ fx, onExpire }: { fx: FxInstance; onExpire: (id: string) => void }) {
  const progressRef = useRef(0);
  const elapsedRef = useRef(0);
  const expiredRef = useRef(false);

  useFrame((_, delta) => {
    elapsedRef.current += delta;
    progressRef.current = Math.min(elapsedRef.current / fx.duration, 1);
    if (progressRef.current >= 1 && !expiredRef.current) {
      expiredRef.current = true;
      onExpire(fx.id);
    }
  });

  return renderEffect(fx, progressRef);
}

function renderEffect(fx: FxInstance, progressRef: React.MutableRefObject<number>) {
  switch (fx.kind) {
    case "mergeFlash":
      return <MergeFlash fx={fx} progressRef={progressRef} />;
    case "reactionFlash":
      return <ReactionFlash fx={fx} progressRef={progressRef} />;
    case "heatShimmer":
      return <HeatShimmer fx={fx} progressRef={progressRef} />;
    case "warningPulse":
      return <WarningPulse fx={fx} progressRef={progressRef} />;
    case "explosion":
      return <Explosion fx={fx} progressRef={progressRef} />;
    case "pressureJudder":
      return <PressureJudder fx={fx} progressRef={progressRef} />;
    case "burstSpill":
      return <BurstSpill fx={fx} progressRef={progressRef} />;
    case "corrosionMark":
      return <CorrosionMark fx={fx} progressRef={progressRef} />;
    case "leakDribble":
      return <LeakDribble fx={fx} progressRef={progressRef} />;
    case "poisonPuff":
      return <PoisonPuff fx={fx} progressRef={progressRef} />;
    case "sludgeBubble":
      return <SludgeBubble fx={fx} progressRef={progressRef} />;
    case "cardSlam":
      return <CardSlam fx={fx} progressRef={progressRef} />;
    case "objectiveTick":
      return <ObjectiveTick fx={fx} progressRef={progressRef} />;
    default:
      return assertNever(fx.kind, "renderEffect");
  }
}

/** Hook that owns the live FX list; spawn() is wired to the driver's events. */
export function useFxPool() {
  const [instances, setInstances] = useState<FxInstance[]>([]);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const spawn = (next: FxInstance[]) => {
    if (next.length === 0 || !mountedRef.current) return;
    setInstances((prev) => [...prev, ...next]);
  };

  const expire = (id: string) => {
    if (!mountedRef.current) return;
    setInstances((prev) => prev.filter((fx) => fx.id !== id));
  };

  const clear = () => setInstances([]);

  return { instances, spawn, expire, clear };
}
